import { Mark, mergeAttributes } from "@tiptap/core";
import { CodeBlock as CodeBlockBase } from "@tiptap/extension-code-block";
import { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DecorationSet } from "@tiptap/pm/view";
import { v4 as uuid } from "uuid";

import { nodeHasCodeMark, nodeHasHighlightMark } from "@/wc/editor/utils/nodes.ts";
import { addToClipboard } from "@/utils/clipboard.ts";
import { NWCodeBlockProps } from "./index.tsx";
import { Store } from "@/store";

import { SyntaxHighlightPlugin } from "./syntaxPlugin.ts";

export const NWCodeAdapter = Mark.create<NWCodeBlockProps>({
  name: "code",
  draggable: false,
  code: true,
  marks: "highlight",
  selectable: true,
  keepOnSplit: true,

  parseHTML() {
    return [
      {
        tag: "nw-code-block[inline=true]",
        preserveWhitespace: "full",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["nw-code-block", mergeAttributes(HTMLAttributes, { inline: true }), 0];
  },

  addAttributes() {
    return {
      language: {
        default: "shell",
      },
      inline: {
        default: true,
      },
      copyable: {
        default: true,
      },
      codegroup: {
        default: null,
        parseHTML: element => element.getAttribute("codegroup"),
        renderHTML: attributes => {
          if (!attributes.codegroup) {
            attributes.codegroup = `${uuid()}`;
          }
          return {
            codegroup: attributes.codegroup,
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: new PluginKey("nw-code-inline"),
        props: {
          handleClick: (view, pos, event) => {
            const node = view.state.doc.nodeAt(pos);

            if (node == null || !node.isText || !nodeHasCodeMark(node) || nodeHasHighlightMark(node)) {
              return false;
            }

            const domAtPos = view.domAtPos(pos);
            if (!domAtPos) return false;

            const clickedElement = event.target as HTMLElement;
            const codeBlockElement = clickedElement.closest("nw-code-block");

            // Check if click was actually inside a code block element
            if (!codeBlockElement) {
              return false;
            }

            const codegroup = node.marks.find(mark => mark.type.name === "code")?.attrs.codegroup;

            if (codegroup) {
              const nodes: Node[] = [];
              view.state.doc.descendants(node => {
                if (node.marks.some(mark => mark.type.name === "code" && mark.attrs.codegroup === codegroup)) {
                  nodes.push(node);
                }

                return true;
              });

              const text = nodes.map(n => getTextFromNode(n)).join("");
              addToClipboard(text);
              showCopiedTooltip(event);

              import("@/services/analyticsService").then(({ useAnalyticsService }) => {
                useAnalyticsService().trackCodeSnippetCopied("inline", Store.project.id.value!, text);
              });
            }
          },
        },
      }),
    ];
  },
});

function getTextFromNode(node: Node) {
  if (node.type.name === "nw-variable") {
    const variableKey = node.attrs.name;
    const variable = Store.project.variables.get(variableKey);
    return variable || node.attrs.placeholder || "";
  }

  return node.text || "";
}

export const NWCodeBlockAdapter = CodeBlockBase.extend({
  marks: "highlight",
  content: "inline*",

  renderHTML({ HTMLAttributes }) {
    return ["nw-code-block", mergeAttributes(HTMLAttributes, { inline: false }), 0];
  },

  addAttributes() {
    return {
      language: {
        default: "shell",
      },
      inline: {
        default: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-code-block[inline=false]",
        getAttrs: node => {
          return {
            language: node.getAttribute("class") ? getLanguageFromClass(node.getAttribute("class")!) : "shell",
          };
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() || []),
      SyntaxHighlightPlugin({
        name: this.name,
        defaultLanguage: this.options.defaultLanguage,
      }),
    ];
  },
});

function getLanguageFromClass(className: string) {
  return className.replace("language-", "");
}

function showCopiedTooltip(event: MouseEvent) {
  const tooltip = document.createElement("nw-tooltip");
  tooltip.setAttribute("content", "Copied!");
  tooltip.setAttribute("placement", "top");
  tooltip.setAttribute("trigger", "click");
  tooltip.style.position = "fixed";

  tooltip.style.left = `${event.clientX}px`;
  tooltip.style.top = `${event.clientY - 12}px`;

  const triggerElement = document.createElement("div");
  triggerElement.style.position = "absolute";
  triggerElement.style.width = "0px";
  triggerElement.style.height = "0px";
  triggerElement.style.left = `${0}px`;
  triggerElement.style.top = `${0}px`;
  tooltip.appendChild(triggerElement);

  document.body.appendChild(tooltip);

  triggerElement.click();

  setTimeout(() => {
    document.body.removeChild(tooltip);
  }, 1500);
}
